import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useWatch } from "react-hook-form";

import BaseStepper from "@/components_v2/Stepper/BaseStepper";
import SurveyViewFormGeneralInfo from "@/features/survey-views/form/components/form-steps/SurveyViewFormGeneralInfo";
import SurveyViewFormContent from "@/features/survey-views/form/components/form-steps/SurveyViewFormContent";
import SurveyViewFormCustomization from "@/features/survey-views/form/components/form-steps/SurveyViewFormCustomization";
import BaseButton from "@/components_v2/Button/BaseButton";
import {
  SurveyViewFormProps,
  SurveyViewFormValues,
} from "@/features/survey-views/form/types";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import EvidenceNotificationModal from "@/features/survey-views/form/components/EvidenceNotificationModal";
import { SharingRights } from "@/shared/utils/constants";
import { BUTTON_VARIANTS } from "@/components_v2/Button/constants";

import "./styles.css";

const SurveyViewForm = ({
  form,
  handleSubmit,
  originalEvidenceIds = [],
}: SurveyViewFormProps) => {
  const { t } = useTranslation();
  const SurveyViewFormStepsCount = 3;

  const [isEvidenceModalOpen, setIsEvidenceModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const surveyViewId = useWatch({ control: form.control, name: "id" });
  const currentSelectedAnswers = useWatch({
    control: form.control,
    name: "answers",
  });
  const stepValidationFields = [
    ["survey", "title", "config.dataFormat", "config.distributionFormat"],
    ["answers"],
    [],
  ];

  const newlySelectedPrivateAndCommunityEvidenceIds = useMemo(() => {
    const currentIds = currentSelectedAnswers.reduce<number[]>(
      (acc, answer) => {
        answer.attachedBlocks.forEach((block) => {
          if (
            [
              SharingRights.PRME_COMMUNITY.value,
              SharingRights.PRIVATE.value,
            ].includes(block.sharingRights)
          ) {
            acc.push(block.id);
          }
        });
        return acc;
      },
      [],
    );

    // Return only new evidence IDs that weren't in the original
    return currentIds.filter((id) => !originalEvidenceIds.includes(id));
  }, [currentSelectedAnswers, originalEvidenceIds]);

  const handleNext = async () => {
    const fieldNames = stepValidationFields[currentStep];
    if (!fieldNames) return;
    const isValid = await form.trigger(
      fieldNames as unknown as keyof SurveyViewFormValues,
    );
    if (!isValid) return;

    if (currentStep < SurveyViewFormStepsCount - 1) {
      setCurrentStep(currentStep + 1);
    } else if (currentStep === SurveyViewFormStepsCount - 1) {
      if (newlySelectedPrivateAndCommunityEvidenceIds.length === 0) {
        await handleSubmit();
      } else {
        setIsEvidenceModalOpen(true);
      }
    }
  };

  const handleConfirm = async () => {
    setIsEvidenceModalOpen(false);
    await handleSubmit();
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="flex flex-col items-center">
      <div className="survey-view-form-container flex flex-col gap-400 full-width">
        <h2 className="header-m-medium">
          {surveyViewId
            ? t("surveyViews.labels.editSurveyView")
            : t("surveyViews.labels.createSurveyView")}
        </h2>

        <BaseStepper
          current={currentStep}
          items={[
            {
              title: t("surveyViews.labels.generalInformation"),
            },
            {
              title: t("surveyViews.labels.surveyContent"),
            },
            {
              title: t("surveyViews.labels.customization"),
            },
          ]}
        />
        {currentStep === 0 && <SurveyViewFormGeneralInfo form={form} />}
        {currentStep === 1 && <SurveyViewFormContent form={form} />}
        {currentStep === 2 && <SurveyViewFormCustomization form={form} />}

        <div className="flex justify-between items-center">
          <BaseButton
            variant={BUTTON_VARIANTS.TERTIARY}
            key="back"
            onClick={() => {
              handlePrevious();
            }}
            disabled={currentStep === 0}
          >
            {t("buttons.back")}
          </BaseButton>

          <BaseButton
            variant={BUTTON_VARIANTS.PRIMARY}
            key="next"
            onClick={handleNext}
            disabled={form.formState.isSubmitting}
          >
            {form.formState.isSubmitting ? (
              <>
                <BaseSpinner size="small" color="white" />
                {t("buttons.generating")}
              </>
            ) : currentStep === SurveyViewFormStepsCount - 1 ? (
              t("buttons.generateView")
            ) : (
              t("buttons.next")
            )}
          </BaseButton>
        </div>
      </div>
      <EvidenceNotificationModal
        open={isEvidenceModalOpen}
        setOpen={setIsEvidenceModalOpen}
        evidenceIds={newlySelectedPrivateAndCommunityEvidenceIds}
        handleConfirm={handleConfirm}
      />
    </div>
  );
};

export default SurveyViewForm;
